import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { apiChat } from '../http';

/**
 * Busca os detalhes de um chat específico pelo seu ID.
 * @param {string} id - O ID do chat a ser buscado.
 */
export function useChatDetailsByChatId(id) {
  const [state, setState] = useState({
    response: null,
    isLoading: true,
    error: null
  });

  const fetchChatDetails = useCallback(async () => {
    if (!id) {
      setState({ response: null, isLoading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const { data: apiResponse } = await apiChat.get(`/monitoramento/${id}`);

      try {
        const validatedData = CHAT_DETAILS_SCHEMA.validateSync(apiResponse, {
          stripUnknown: true
        });
        setState({ response: validatedData, isLoading: false, error: null });
      } catch (validationError) {
        const errorMessage =
          validationError.message || 'Erro ao validar os dados do chat.';
        toast.error(errorMessage);
        console.error('Validation Error:', validationError);
        setState({ response: null, isLoading: false, error: errorMessage });
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || 'Erro ao buscar detalhes do chat.';
      toast.error(errorMessage);
      console.error(err);
      setState({ response: null, isLoading: false, error: errorMessage });
    }
  }, [id]);

  useEffect(() => {
    fetchChatDetails();
  }, [fetchChatDetails]);

  return { ...state, refetch: fetchChatDetails };
}

const CHAT_DETAILS_SCHEMA = yup
  .object({
    _id: yup.string().required(),
    id: yup.string().required(),
    isOnHold: yup.boolean().nullable().default(false),
    nova_mensagem: yup.boolean().required(),
    canal: yup.string().required(),
    apelidoCanal: yup.string().required(),
    encerrada: yup.boolean().required(),
    atendida: yup.boolean().required(),
    situacao: yup.string().required(),
    fila: yup.string().required(),
    queueId: yup.number().required(),
    cliente: yup
      .object({
        id: yup.string().optional(),
        nome: yup.string().required(),
        telefone: yup.string().optional().nullable(),
        id_whatsapp: yup.string().nullable()
      })
      .required(),
    hora_criacao: yup.string().required(),
    mensagens: yup
      .array(
        yup.object({
          hora_da_mensagem: yup.string().required(),
          response_type: yup.string().required(),
          cliente_ou_atendente: yup.string().required(),
          escrita_por: yup.string().required(),
          texto: yup.string().optional().nullable().default(''),
          is_deleted: yup.boolean().required(),
          actions: yup
            .object({
              action: yup.string().optional(),
              agent: yup.string().optional(),
              queue: yup.string().optional()
            })
            .optional()
        })
      )
      .required(),
    atendente: yup
      .object({
        id_postgres: yup.string().required(),
        name: yup.string().required()
      })
      .nullable(),
    hora_do_atendimento: yup.string().nullable(),
    timeline: yup
      .array(
        yup.object({
          atividade: yup.string().required(),
          descricao: yup.string().required(),
          horaAtividade: yup.string().required()
        })
      )
      .required(),
    statusOfChat: yup.string().nullable()
  })
  .typeError('O formato dos dados recebidos do servidor é inválido.');
